import React, { MouseEventHandler, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import UserAvatar, { AvatarSize } from 'Douleutaras/components/UserAvatar/UserAvatar';
import CloseButton from 'Douleutaras/components/Buttons/CloseButton';
import { useTranslation } from 'react-i18next';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';
import DefaultModalBackground from 'Douleutaras/components/modals/DefaultModalBackground';

import './DefaultModal.scss';

// Example Usage:
//
//   <Switch>
//     <Route path={'*/modal'}>
//       <Modal isOpen dismissCallback={() => true}>
//         <DefaultModal
//           serviceIcon={PopupServiceImg}
//           serviceIconAlt={jobCategory.attributes.title}
//           title="Επικοινώνησε με τον Μενέλαο!"
//           note="Μίλησε μαζί του μέσω chat ή τηλεφώνου για την εργασία σου."
//           backText="Back"
//           backTextAriaLabel="Go Back"
//           closeAriaLabel="Close window"
//           closeButtonCallback={() => true}
//           backButtonCallback={() => true}
//         >
//           <ul>
//             <li>Hello</li>
//             <li>World</li>
//           </ul>
//         </DefaultModal>
//       </Modal>
//     </Route>
//   </Switch>

const DefaultModal =
  ({
     serviceIcon,
     serviceIconAlt,
     sideInfo,
     title,
     note,
     backText,
     backTextAriaLabel,
     closeAriaLabel,
     children,
     closeButtonCallback,
     backButtonCallback,
     showBackButton = true,
     fullHeight = false,
     footer,
     footerDisableLine,
     userMugshot,
     noInnerVerticalPaddings = false,
     wide = false,
     bottomText
  }: {
    serviceIcon?: string, // The illustration shown on the bottom left of the modal
    serviceIconAlt?: string | React.ReactElement, // Alt text of illustration
    sideInfo?: React.ReactElement, // Box on the left side of the modal
    title?: string | React.ReactElement, // Title of modal
    note?: string | React.ReactElement, // Body text of modal
    backText?: string  | React.ReactElement, // Text shown on the "back" button on mobilee view
    backTextAriaLabel?: string | React.ReactElement, // Aria Label for back button
    closeAriaLabel?: string | React.ReactElement, // Aria label for Close Icon on top right
    children?: React.ReactNode,
    closeButtonCallback?: MouseEventHandler,
    backButtonCallback?: MouseEventHandler,
    showBackButton?: boolean,
    fullHeight?: boolean,
    footer?: string | React.ReactElement,
    footerDisableLine?: boolean, // Disables the line separating footer and main body
    userMugshot?: string,
    noInnerVerticalPaddings?: boolean,
    wide?: boolean,
    bottomText?: string | React.ReactElement // This element would be rendered on very bottom of modal
  }) => {

  const isMobile = useMediaQuery(
    { maxWidth: TABLET_BIG.MIN_WIDTH }
  );

  const { t } = useTranslation();

  const [modalHeight, setModalHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setModalHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

    // tslint:disable-next-line:no-null-keyword
  const mobileStyles = isMobile ? { height: `${modalHeight}px` } : null;

  return (
    <article styleName="modal" style={mobileStyles}>
      <div styleName="modal-grid">
        { showBackButton &&
          <section styleName="modal-prev">
            { /* @ts-ignore */ }
            <button styleName="modal-prev__btn" type="button" aria-label={backTextAriaLabel || t('Back')} onClick={backButtonCallback}>
              {backText}
            </button>
          </section>
        }
        { closeButtonCallback &&
          <section styleName="modal-close">
            { /* @ts-ignore */ }
            <CloseButton onClick={closeButtonCallback} ariaLabel={closeAriaLabel || t('Close')} options={{ withShadow: true }} />
          </section>
        }
        { isMobile && Boolean(userMugshot) &&
          <div styleName="modal-avatar">
            <div styleName="modal-avatar__inner">
              <UserAvatar mugshot={userMugshot} size={AvatarSize.MODAL}/>
            </div>
          </div>
        }
        { sideInfo &&
          sideInfo
        }
        <section styleName={classNames('modal-form-wrapper',
          { 'modal-form-wrapper--side-info': sideInfo,
            'modal-form-wrapper--no-vertical-paddings': noInnerVerticalPaddings }
        )}>
          <div styleName="modal-form">
            <section styleName="modal-content">
              { !isMobile && Boolean(userMugshot) &&
                <div styleName="modal-avatar">
                  <div styleName="modal-avatar__inner">
                    <UserAvatar mugshot={userMugshot} size={AvatarSize.MODAL}/>
                  </div>
                </div>
              }
              <div styleName={
                  classNames('modal-list', {
                    'modal-list--fullHeight': fullHeight,
                    'modal-list--wide': wide
                  })}>
                <div styleName="form-item">
                  <div styleName="form-input">
                    { /* @ts-ignore */ }
                    { (Boolean(title?.props?.children.length) || Boolean(title)) &&
                      <header styleName="form-input__headline">
                        <span styleName="form-input__headline-title">
                          {title}
                        </span>
                        { Boolean(note) &&
                        <span styleName="form-input__headline-subtitle">
                          {note}
                        </span>
                        }
                      </header>
                    }
                    <div styleName="form-input__content">
                      {children}
                    </div>
                  </div>
                </div>
                { bottomText !== undefined
                  ? <div styleName="form-spacer">{bottomText}</div>
                  : <div styleName="form-spacer" />
                }
              </div>
            </section>
            { footer !== undefined &&
              <section styleName={ classNames({ 'modal-action': true, 'modal-action--no-line': footerDisableLine })}>
                {footer}
              </section>
            }
          </div>
        </section>
        <section styleName="modal-service">
          { /* @ts-ignore */ }
          { serviceIcon && <img styleName="modal-service__img" src={serviceIcon} alt={serviceIconAlt} /> }
        </section>
        <DefaultModalBackground hasSideInfo={!!sideInfo} />
      </div>
    </article>
  );
};

export default DefaultModal;
